import { FC, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../auth/authContext';
import { useAnalyzeService } from '../../services/useAnalyzeService';
import { useCandidateComparisonService } from '../../services/useCandidateComparisonService';
import { CandidateComparisonResponse, CandidateComparisonReviewOption } from '../../types/candidateComparison';
import { JobPostingDto } from '../../types/jobPostingDto';
import './candidatecomparisonpage.css';

const CandidateComparisonPage: FC = () => {
    const [searchParams] = useSearchParams();
    const { user } = useAuth();
    const { getPastJobPostingsAnalysis } = useAnalyzeService();
    const { getReviewOptions, compareCandidates } = useCandidateComparisonService();
    const [jobs, setJobs] = useState<JobPostingDto[]>([]);
    const [jobPostingId, setJobPostingId] = useState(searchParams.get('jobPostingId') ?? '');
    const [reviews, setReviews] = useState<CandidateComparisonReviewOption[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [loadingReviews, setLoadingReviews] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [result, setResult] = useState<CandidateComparisonResponse | null>(null);

    useEffect(() => {
        if (!user?.id) return;
        getPastJobPostingsAnalysis(user.id).then(setJobs).catch(() => setJobs([]));
    }, [user?.id, getPastJobPostingsAnalysis]);

    useEffect(() => {
        setSelectedIds([]);
        setResult(null);
        setReviews([]);
        if (!jobPostingId) return;
        setLoadingReviews(true);
        getReviewOptions(jobPostingId)
            .then(setReviews)
            .catch(() => setError('Unable to load completed recruiter reviews for this job.'))
            .finally(() => setLoadingReviews(false));
    }, [jobPostingId, getReviewOptions]);

    const labels = useMemo(
        () => Object.fromEntries(reviews.map(review => [review.recruiter_review_id, review.candidate_label])),
        [reviews],
    );

    const toggleReview = (id: string) => {
        setSelectedIds(current => current.includes(id)
            ? current.filter(value => value !== id)
            : current.length < 5 ? [...current, id] : current);
    };

    const runComparison = async () => {
        if (!jobPostingId || selectedIds.length < 2 || selectedIds.length > 5) return;
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            setResult(await compareCandidates({
                job_posting_id: jobPostingId,
                recruiter_review_ids: selectedIds,
            }));
        } catch (err: any) {
            const data = err?.response?.data;
            setError(data?.message ?? data?.detail ?? err?.message ?? 'Candidate comparison failed.');
        } finally {
            setLoading(false);
        }
    };

    const priorityFor = (candidateId: string) =>
        result?.interview_priorities.find(item => item.candidate_id === candidateId)?.priorities ?? [];

    return (
        <div className="cc-page">
            <header className="cc-header">
                <div className="cc-eyebrow">Evidence over keywords</div>
                <h1>Candidate Comparison</h1>
                <p>Compare completed recruiter reviews for one role using evidence strength, uncertainty, and interview risk.</p>
            </header>

            <section className="cc-panel cc-setup">
                <label htmlFor="cc-job">Job posting</label>
                <select id="cc-job" value={jobPostingId} onChange={event => setJobPostingId(event.target.value)}>
                    <option value="">— Select a saved job —</option>
                    {jobs.map(job => (
                        <option key={job.jobPostingId} value={job.jobPostingId}>{job.title} — {job.company}</option>
                    ))}
                </select>

                <div className="cc-selection-heading">
                    <div>
                        <strong>Completed recruiter reviews</strong>
                        <span>Select 2–5 reviews for the same role.</span>
                    </div>
                    <span className="cc-count">{selectedIds.length}/5 selected</span>
                </div>

                {loadingReviews && <div className="cc-empty">Loading reviews…</div>}
                {!loadingReviews && jobPostingId && reviews.length === 0 && (
                    <div className="cc-empty">Run at least two Recruiter Reviews for this job before comparing candidates.</div>
                )}
                <div className="cc-review-grid">
                    {reviews.map(review => {
                        const selected = selectedIds.includes(review.recruiter_review_id);
                        return (
                            <button
                                type="button"
                                key={review.recruiter_review_id}
                                className={`cc-review-option ${selected ? 'selected' : ''}`}
                                onClick={() => toggleReview(review.recruiter_review_id)}
                                aria-pressed={selected}
                            >
                                <span className="cc-check">{selected ? '✓' : ''}</span>
                                <span className="cc-review-main">
                                    <strong>{review.candidate_label}</strong>
                                    <small>{new Date(review.created_utc).toLocaleString()}</small>
                                </span>
                                <span className="cc-review-score">{review.candidate_match_score}</span>
                                <span className={`cc-confidence ${review.submission_confidence.toLowerCase()}`}>
                                    {review.submission_confidence}
                                </span>
                            </button>
                        );
                    })}
                </div>

                <div className="cc-actions">
                    {error ? <span className="cc-error">{error}</span> : <span />}
                    <button
                        type="button"
                        className="cc-run"
                        disabled={selectedIds.length < 2 || loading}
                        onClick={runComparison}
                    >
                        {loading ? 'Comparing evidence…' : 'Compare Candidates'}
                    </button>
                </div>
            </section>

            {result && (
                <div className="cc-results">
                    <section className="cc-panel cc-summary">
                        <div className="cc-section-label">Comparison summary</div>
                        <p>{result.overall_summary}</p>
                    </section>

                    <section className="cc-rankings">
                        {[...result.candidate_rankings].sort((a, b) => a.rank - b.rank).map(candidate => (
                            <article className="cc-rank-card" key={candidate.candidate_id}>
                                <div className="cc-rank-top">
                                    <span className="cc-rank-number">#{candidate.rank}</span>
                                    <div>
                                        <h2>{labels[candidate.candidate_id] ?? 'Candidate review'}</h2>
                                        <span>{candidate.overall_match_score}% evidence-aligned</span>
                                    </div>
                                </div>
                                <div className="cc-risk-grid">
                                    <span>Evidence <b>{candidate.evidence_confidence}</b></span>
                                    <span>Verification <b>{candidate.verification_required}</b></span>
                                    <span>Interview risk <b>{candidate.interview_risk}</b></span>
                                </div>
                                <p>{candidate.ranking_explanation}</p>
                                <div className="cc-card-columns">
                                    <div><h3>Differentiators</h3><ul>{candidate.strongest_differentiators.map((item, i) => <li key={i}>{item}</li>)}</ul></div>
                                    <div><h3>Concerns</h3><ul>{candidate.primary_concerns.map((item, i) => <li key={i}>{item}</li>)}</ul></div>
                                </div>
                                <div className="cc-priorities">
                                    <h3>Interview priorities</h3>
                                    <ul>{priorityFor(candidate.candidate_id).map((item, i) => <li key={i}>{item}</li>)}</ul>
                                </div>
                            </article>
                        ))}
                    </section>

                    <section className="cc-panel cc-matrix-wrap">
                        <div className="cc-section-label">Side-by-side evidence</div>
                        <div className="cc-table-scroll">
                            <table className="cc-matrix">
                                <thead><tr><th>Requirement</th>{result.candidate_rankings.map(candidate => <th key={candidate.candidate_id}>{labels[candidate.candidate_id]}</th>)}</tr></thead>
                                <tbody>
                                    {result.skill_comparison.map(skill => (
                                        <tr key={skill.skill_name}>
                                            <td><strong>{skill.skill_name}</strong><small>{skill.requirement_level}</small></td>
                                            {result.candidate_rankings.map(candidate => {
                                                const assessment = skill.candidates.find(item => item.candidate_id === candidate.candidate_id);
                                                return <td key={candidate.candidate_id}>
                                                    <span className={`cc-strength ${(assessment?.evidence_strength ?? 'None').toLowerCase()}`}>{assessment?.evidence_strength ?? 'None'}</span>
                                                    <p>{assessment?.summary}</p>
                                                    <small>{assessment?.confidence} confidence</small>
                                                </td>;
                                            })}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
            )}
        </div>
    );
};

export default CandidateComparisonPage;
